import { faFilter } from '@fortawesome/pro-light-svg-icons/faFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Checkbox from 'components/common/checkbox';
import connect from 'components/lib/connect';
import ConversationHistoryFilter from 'models/conversation_history_filter';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import SetConversationHistoryFilter from 'actions/customer/feed/set_conversation_history_filter';
import TYPE_GROUPS, { groupLabel } from './filtering/conversation_item_type_groups';
import { useMobilePanel } from 'components/mobile/mobile_context';

export function ConversationHistoryFilterBar({ filter, setFilter }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { mobilePanel } = useMobilePanel();

  // On a phone the other panels open as an overlay on the feed, and this bar floats above it. It only
  // filters the conversation, so it goes away with the conversation.
  if (mobilePanel) return null;

  // Filter reset on customer change is handled by ConversationHistory (see the
  // customerId-keyed effect there), not here — this component unmounts on
  // loading flickers for the same customer, which would wipe an active filter.

  // `filter.address` has no UI right now — the endpoint-address section was removed from this menu —
  // but the model and item_passes_filter still support it, so it stays threaded through here (and in
  // `isFiltering`, via the model's isActive) for when the filter returns. Don't prune it as dead code.
  const isFiltering = filter.isActive();

  // The badge counts the boxes that are checked (the types being shown), not the ones hidden.
  const selectedTypeGroupCount = TYPE_GROUPS.length - filter.hiddenTypeGroups.length;

  const sortedGroups = [...TYPE_GROUPS].sort((a, b) => groupLabel(t, a.key).localeCompare(groupLabel(t, b.key)));

  const toggleGroup = groupKey => {
    const hiddenTypeGroups = filter.isTypeGroupHidden(groupKey)
      ? filter.hiddenTypeGroups.filter(key => key !== groupKey)
      : [...filter.hiddenTypeGroups, groupKey];
    setFilter(ConversationHistoryFilter.create({ address: filter.address, hiddenTypeGroups }));
  };

  const deselectAll = () =>
    setFilter(
      ConversationHistoryFilter.create({
        address: filter.address,
        hiddenTypeGroups: TYPE_GROUPS.map(group => group.key),
      })
    );

  const reset = () => setFilter(ConversationHistoryFilter.create());

  return (
    <OutsideClickHandler onClickOutside={() => setIsOpen(false)}>
      <Container>
        <ToggleButton
          active={isFiltering}
          data-aid="conversationFilter-toggle"
          onClick={() => setIsOpen(open => !open)}
          type="button"
        >
          <FilterIcon icon={faFilter} />
          {t('Filter')}
          {isFiltering ? ` (${selectedTypeGroupCount})` : ''}
        </ToggleButton>
        {isOpen && (
          <Panel data-aid="conversationFilter-panel">
            <SectionHeaderRow>
              <SectionTitle>{t('Show item types')}</SectionTitle>
              {isFiltering ? (
                <TextButton data-aid="conversationFilter-reset" onClick={reset} type="button">
                  {t('Reset')}
                </TextButton>
              ) : (
                <TextButton data-aid="conversationFilter-deselectAll" onClick={deselectAll} type="button">
                  {t('Deselect all')}
                </TextButton>
              )}
            </SectionHeaderRow>
            {sortedGroups.map(group => (
              <CheckboxRow key={group.key}>
                <Checkbox
                  checked={!filter.isTypeGroupHidden(group.key)}
                  data-aid={`conversationFilter-type-${group.key}`}
                  label={groupLabel(t, group.key)}
                  onChange={() => toggleGroup(group.key)}
                />
              </CheckboxRow>
            ))}
          </Panel>
        )}
      </Container>
    </OutsideClickHandler>
  );
}

ConversationHistoryFilterBar.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
};

function mapStateToProps({ getProvider }) {
  return {
    filter: getProvider('conversationHistoryFilter').get(),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    setFilter: filter => executeAction(SetConversationHistoryFilter, filter),
  };
}

const Container = styled.div`
  left: 16px;
  position: absolute;
  top: 11px;
  z-index: 6;
`;

const ToggleButton = styled.button`
  background: ${p => (p.active ? p.theme.colors.green400 : p.theme.colors.white)};
  border: 1px solid ${p => (p.active ? p.theme.colors.green400 : p.theme.colors.gray400)};
  border-radius: 16px;
  color: ${p => (p.active ? p.theme.colors.white : p.theme.colors.gray800)};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.base};
  font-weight: ${p => p.theme.fontWeight.medium};
  padding: 4px 12px;
`;

const FilterIcon = styled(FontAwesomeIcon)`
  margin-right: 6px;
`;

const TextButton = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.green500};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.small};
  font-weight: ${p => p.theme.fontWeight.medium};
  padding: 0;
`;

const Panel = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 8px;
  box-shadow: ${p => p.theme.boxShadow.medium};
  margin-top: 6px;
  min-width: 240px;
  padding: 12px 16px;
`;

const SectionHeaderRow = styled.div`
  align-items: baseline;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.small};
  font-weight: ${p => p.theme.fontWeight.medium};
  text-transform: uppercase;
`;

const CheckboxRow = styled.div`
  margin-bottom: 6px;
`;

export default connect(mapStateToProps, mapExecuteToProps)(ConversationHistoryFilterBar);
